import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchIngredientStockReport,
  exportIngredientStockReport,
  clearError,
} from 'store/slices/reportSlice';
import { fetchInventoryCategories, fetchAllInventorySubcategories, fetchVendorDropdown, fetchCanteenDropdown } from 'store/slices/commonSlice';
import { fetchVendors } from 'store/slices/vendorSlice';

import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Package, AlertTriangle, IndianRupee, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import FormField from 'components/common/FormField';

const IngredientStockReportPage = () => {
  const dispatch = useDispatch();

  const {
    ingredientStockReport = { data: { summary: {}, results: [] }, pagination: null },
    isLoading,
    error,
  } = useSelector((state) => state.report);

  const summary = ingredientStockReport.data.summary || {};
  const results = ingredientStockReport.data.results || [];
  const pagination = ingredientStockReport.pagination || {};


  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    category: 'all',
    sub_category: 'all',
    vendor: 'all',
    low_stock_only: false,
    min_stock: '',
    max_stock: '',
    min_buffer: '',
    max_buffer: '',
  });

  // Get data from Redux state
  const inventoryCategories = useSelector((state) => state.common?.inventoryCategories?.items || []);
  const categoriesLoading = useSelector((state) => state.common?.inventoryCategories?.loading || false);

  const subcategories = useSelector((state) => state.common?.subcategories?.items || []);


  const vendors = useSelector((state) => state.common?.vendors?.items || []);
  const vendorsLoading = useSelector((state) => state.vendor?.isLoading || false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);



  // Calculate total pages from server response with fallback
  const totalPages = pagination.total_pages || 1;

  // Check if any advanced filters are active (excluding 'all' values)
  const hasActiveAdvancedFilters = Object.values(advancedFilters).some(value =>
    value !== '' && value !== 'all' && value !== false
  );

  const refetch = useCallback(() => {
    // Build the API params object with all filters
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      report_type: 'ingredient_stock',
      ...dateFilters,
      // Add advanced filters with proper naming for the backend
      ...(advancedFilters.category !== 'all' && { category: advancedFilters.category }),
      ...(advancedFilters.sub_category !== 'all' && { sub_category: advancedFilters.sub_category }),
      ...(advancedFilters.vendor !== 'all' && { vendor: advancedFilters.vendor }),
      ...(advancedFilters.low_stock_only && { low_stock_only: advancedFilters.low_stock_only }),
      ...(advancedFilters.min_stock && { min_stock: advancedFilters.min_stock }),
      ...(advancedFilters.max_stock && { max_stock: advancedFilters.max_stock }),
      ...(advancedFilters.min_buffer && { min_buffer: advancedFilters.min_buffer }),
      ...(advancedFilters.max_buffer && { max_buffer: advancedFilters.max_buffer }),
    };


    dispatch(fetchIngredientStockReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Reset to page 1 when search term, page size, or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  // Fetch all data on component mount
  useEffect(() => {
    dispatch(fetchInventoryCategories());
    dispatch(fetchCanteenDropdown());
    dispatch(fetchVendorDropdown());
  }, [dispatch]);


  // --- Event Handlers ---
  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [key]: value,
      // Reset subcategory when category changes
      ...(key === 'category' && { sub_category: 'all' })
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      category: 'all',
      sub_category: 'all',
      vendor: 'all',
      low_stock_only: false,
      min_stock: '',
      max_stock: '',
      min_buffer: '',
      max_buffer: '',
    });
  };

  const columns = [
    {
      accessorKey: 'category',
      header: 'Category',
      cell: ({ row }) => (
        <Badge variant="outline" className="font-medium">
          {row.original.category}
        </Badge>
      )
    },
    {
      accessorKey: 'sub_category',
      header: 'Sub Category / Ingredient',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{row.original.sub_category}</span>
        </div>
      )
    },
    {
      accessorKey: 'current_stock',
      header: 'Current Stock',
      cell: ({ row }) => (
        <Badge variant={row.original.is_low_stock ? 'destructive' : 'default'}>
          {row.original.current_stock}
        </Badge>
      ),
    },
    {
      accessorKey: 'buffer_stock_alert',
      header: 'Buffer Alert Level',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.buffer_stock_alert || 'None'}</span>
      ),
    },
    {
      accessorKey: 'is_low_stock',
      header: 'Low Stock',
      cell: ({ row }) => (
        <Badge variant={row.original.is_low_stock ? 'destructive' : 'secondary'}>
          {row.original.is_low_stock ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      accessorKey: 'vendor',
      header: 'Vendor',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.vendor || '-'}</span>
      )
    },
    // { 
    //   accessorKey: 'purchased_on', 
    //   header: 'Purchased On',
    //   cell: ({ row }) => {
    //     const date = row.original.purchased_on;
    //     return date ? format(new Date(date), 'dd MMM yyyy') : '-';
    //   }
    // },
    {
      accessorKey: 'purchased_on',
      header: 'Purchased On',
      cell: ({ row }) => (
        <span className="font-medium">
          {row.original.purchased_on}
        </span>
      )
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Ingredient Stock Report"
        description="Current stock levels of ingredients with low stock alerts"
      />

      <LogDateFilter onFilter={(f) => setDateFilters(f)} />

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
              >
                {isAdvancedFilterExpanded ? (
                  <>
                    Hide <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={advancedFilters.category}
                  onValueChange={(value) => handleAdvancedFilterChange('category', value)}
                  disabled={categoriesLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={categoriesLoading ? "Loading..." : "Select category"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {Array.isArray(inventoryCategories) && inventoryCategories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sub_category">Sub Category</Label>
                <Select
                  value={advancedFilters.sub_category}
                  onValueChange={(value) => handleAdvancedFilterChange('sub_category', value)}

                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select subcategory"
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Subcategories</SelectItem>
                    {Array.isArray(subcategories) && subcategories.map((subcategory) => (
                      <SelectItem key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="vendor">Vendor</Label>
                <Select
                  value={advancedFilters.vendor}
                  onValueChange={(value) => handleAdvancedFilterChange('vendor', value)}
                  disabled={vendorsLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={vendorsLoading ? "Loading..." : "Select vendor"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Vendors</SelectItem>
                    {Array.isArray(vendors) && vendors.map((vendor) => (
                      <SelectItem key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div> */}

              {/* <FormField
  label="Category"
  name="category"
  type="select"
  value={advancedFilters.category}
  onChange={(e) => handleAdvancedFilterChange('category', e.target.value)}
  disabled={categoriesLoading}
  options={[
    { value: 'all', label: 'All Categories' },
    ...(Array.isArray(inventoryCategories)
      ? inventoryCategories.map((cat) => ({
          value: String(cat.id),
          label: cat.name,
        }))
      : []),
  ]}
  placeholder={categoriesLoading ? "Loading..." : "Select category"}
/> */}

              <FormField
                label="Sub Category"
                name="sub_category"
                type="select"
                value={advancedFilters.sub_category}
                onChange={(e) => handleAdvancedFilterChange('sub_category', e.target.value)}
                options={[
                  { value: 'all', label: 'All Subcategories' },
                  ...(Array.isArray(subcategories)
                    ? subcategories.map((sub) => ({
                      value: String(sub.id),
                      label: sub.name,
                    }))
                    : []),
                ]}
                placeholder="Select subcategory"
              />

              <FormField
                label="Vendor"
                name="vendor"
                type="select"
                value={advancedFilters.vendor}
                onChange={(e) => handleAdvancedFilterChange('vendor', e.target.value)}
                disabled={vendorsLoading}
                options={[
                  { value: 'all', label: 'All Vendors' },
                  ...(Array.isArray(vendors)
                    ? vendors.map((vendor) => ({
                      value: String(vendor.id),
                      label: vendor.name,
                    }))
                    : []),
                ]}
                placeholder={vendorsLoading ? "Loading..." : "Select vendor"}
              />


              <div className="space-y-2">
                <Label>Stock Range</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Input
                      type="number"
                      placeholder="Min"
                      value={advancedFilters.min_stock}
                      onChange={(e) => handleAdvancedFilterChange('min_stock', e.target.value)}
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="Max"
                      value={advancedFilters.max_stock}
                      onChange={(e) => handleAdvancedFilterChange('max_stock', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Buffer Alert Range</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Input
                      type="number"
                      placeholder="Min"
                      value={advancedFilters.min_buffer}
                      onChange={(e) => handleAdvancedFilterChange('min_buffer', e.target.value)}
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="Max"
                      value={advancedFilters.max_buffer}
                      onChange={(e) => handleAdvancedFilterChange('max_buffer', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="low_stock_only"
                    checked={advancedFilters.low_stock_only}
                    onChange={(e) => handleAdvancedFilterChange('low_stock_only', e.target.checked)}
                  />
                  <Label htmlFor="low_stock_only">Only show low stock items</Label>
                </div>
              </div> */}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Ingredients</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.total_ingredients || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Low Stock Items</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {summary.low_stock_count || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Current Stock</CardTitle>
            <Package className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {summary.total_current_stock || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Stock Level</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary.total_ingredients > 0
                ? Math.round(summary.total_current_stock / summary.total_ingredients || 0)
                : 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* {isLoading && results.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin mr-3" />
          <span className="text-lg">Loading ingredient stock data...</span>
        </div>
      ) : ( */}
      <DataTable
        data={results} // Use the paginated results from the server
        columns={columns}
        totalCount={pagination.count}
        searchPlaceholder="Search by category, sub-category..."
        onSearch={setSearchTerm}
        showSearch={true}
        pageIndex={page - 1} // Convert to 0-based index for DataTable
        pageSize={pageSize}
        pageCount={totalPages} // Use total pages from server response
        onPageChange={(p) => setPage(p + 1)} // Convert back to 1-based index
        onPageSizeChange={(newSize) => setPageSize(newSize)} // Page reset is handled by useEffect
        isLoading={isLoading}
        onExport={() =>
          dispatch(exportIngredientStockReport({
            filters: {
              page,
              page_size: pageSize,
              search: searchTerm,
              report_type: 'ingredient_stock',
              ...dateFilters,
              // Include advanced filters in export
              ...(advancedFilters.category !== 'all' && { category: advancedFilters.category }),
              ...(advancedFilters.sub_category !== 'all' && { sub_category: advancedFilters.sub_category }),
              ...(advancedFilters.vendor !== 'all' && { vendor: advancedFilters.vendor }),
              ...(advancedFilters.low_stock_only && { low_stock_only: advancedFilters.low_stock_only }),
              ...(advancedFilters.min_stock && { min_stock: advancedFilters.min_stock }),
              ...(advancedFilters.max_stock && { max_stock: advancedFilters.max_stock }),
              ...(advancedFilters.min_buffer && { min_buffer: advancedFilters.min_buffer }),
              ...(advancedFilters.max_buffer && { max_buffer: advancedFilters.max_buffer }),
            }
          }))
        }
        showExport={true}
        showImport={false}
      />
      {/* )} */}
    </div>
  );
};

export default IngredientStockReportPage;